import { getHeadersRequestByServerContext, callMT } from './utils';
import {
  RequestMethod,
  ServerContext,
  GetSharedEntitiesResponse,
  GetSharedEntitiesRequest,
  SetSharedEntityAssociationsResponse,
  SetSharedEntityAssociationsRequest,
  DeleteSharedEntityAssociationsResponse,
  DeleteSharedEntityAssociationsRequest,
  GetSharedEntityAssociationsByEntityIdsResponse,
  GetSharedEntityAssociationsByEntityIdsRequest,
  SharedEntity,
} from './types/api';

class WebsiteListSelectorRepository {
  async getListsByAccountId(serverContext = {} as ServerContext): Promise<GetSharedEntitiesResponse> {
    const headers = getHeadersRequestByServerContext(serverContext);
    const body: GetSharedEntitiesRequest = {
      SharedEntityScope: 'Account',
      SharedEntityType: 'AccountPlacementExclusionList',
      SharedEntityId: headers.CustomerAccountId,
    };

    return callMT(
      `${serverContext.ServicesEndpoints?.CmAPI}v13/SharedEntities/Query`,
      RequestMethod.Post,
      body,
      headers
    );
  }

  async associateListsToCampaign(
    serverContext = {} as ServerContext,
    lists: SharedEntity[],
    campaignId: number
  ): Promise<SetSharedEntityAssociationsResponse> {
    const headers = getHeadersRequestByServerContext(serverContext);

    const previousAssociations = await this.getListsByCampaignId(serverContext, campaignId);

    // Convert arrays to sets for easier comparison
    const previousSet = new Set(previousAssociations.map(({ Id }) => Id));
    const newSet = new Set(lists.map(({ Id }) => Id));

    // Find ids in previous associations but not in new associations
    const removedAssociations = Array.from(previousSet).filter(id => !newSet.has(id));

    // Find ids in new associations but not in previous associations
    const newAssociations = Array.from(newSet).filter(id => !previousSet.has(id));

    // Delete associations that are not in the new list if any
    if (removedAssociations.length > 0) {
      await this.deleteListAssociationsByAccountId(serverContext, removedAssociations, campaignId);
    }

    // if there are no new associations, return early
    if (newAssociations.length === 0) {
      return {};
    }

    const body: SetSharedEntityAssociationsRequest = {
      SharedEntityScope: 'Account',
      Associations: newAssociations.map(id => ({
        SharedEntityId: id,
        SharedEntityType: 'AccountPlacementExclusionList',
        SharedEntityScope: 'Account',
        EntityId: campaignId,
        EntityType: 'Campaign',
      })),
    };

    // Create new associations
    return callMT(
      `${serverContext.ServicesEndpoints?.CmAPI}v13/SharedEntityAssociations/Set`,
      RequestMethod.Post,
      body,
      headers
    );
  }

  async deleteListAssociationsByAccountId(
    serverContext = {} as ServerContext,
    lists: number[],
    campaignId: number
  ): Promise<DeleteSharedEntityAssociationsResponse> {
    const headers = getHeadersRequestByServerContext(serverContext);
    const body: DeleteSharedEntityAssociationsRequest = {
      SharedEntityScope: 'Account',
      Associations: lists.map(id => ({
        SharedEntityId: id,
        SharedEntityType: 'AccountPlacementExclusionList',
        EntityId: campaignId,
        EntityType: 'Campaign',
      })),
    };

    return callMT(
      `${serverContext.ServicesEndpoints?.CmAPI}v13/SharedEntityAssociations`,
      RequestMethod.Delete,
      body,
      headers
    );
  }

  async getListsByCampaignId(
    serverContext = {} as ServerContext,
    payload: number
  ): Promise<SharedEntity[]> {
    try {
      const headers = getHeadersRequestByServerContext(serverContext);
      const body: GetSharedEntityAssociationsByEntityIdsRequest = {
        EntityIds: [payload],
        EntityType: 'Campaign',
        SharedEntityScope: 'Account',
        SharedEntityType: 'AccountPlacementExclusionList',
      };

      const associated: GetSharedEntityAssociationsByEntityIdsResponse = await callMT(
        `${serverContext.ServicesEndpoints?.CmAPI}v13/SharedEntityAssociations/QueryByEntityIds`,
        RequestMethod.Post,
        body,
        headers
      );

      if (associated.Associations.length === 0) {
        return [];
      }

      const queryBody: GetSharedEntitiesRequest = {
        SharedEntityScope: 'Account',
        SharedEntityType: 'AccountPlacementExclusionList',
        SharedEntityId: associated.Associations[0].EntityId,
      };

      const entities: GetSharedEntitiesResponse = await callMT(
        `${serverContext.ServicesEndpoints?.CmAPI}v13/SharedEntities/Query`,
        RequestMethod.Post,
        queryBody,
        headers
      );

      return associated.Associations.map(item =>
        entities.SharedEntities.find(entity => entity.Id === item.SharedEntityId) || { Id: item.SharedEntityId } as SharedEntity);
    } catch (error) {
      return [];
    }
  }
}

export const repository = new WebsiteListSelectorRepository();
